import React, { FC, useEffect, useRef, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useLocalState } from "../../graphql/hooks";

import AceEditor from "react-ace";

import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamation } from "@fortawesome/free-solid-svg-icons";

interface Props {
    jsonData: string;
    updateJsonData: (data: any) => void;
    updating: boolean;
    removeJsonData?: () => void;
    inFullScreen?: boolean;
}

export const EditableJsonView: FC<Props> = ({
    jsonData,
    updateJsonData,
    updating,
    removeJsonData,
    inFullScreen = false
}) => {
    const editorRef = useRef(null);
    const { useDarkMode } = useLocalState();
    const [json, setJson] = useState(jsonData);
    const [canSave, updateCanSave] = useState(false);
    const [hasUnsaved, updateHasUnsaved] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: any) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                e.preventDefault();
                updateCanSave(true);
                updateHasUnsaved(true);
            }
        };
        let editor: any = undefined;
        if (editorRef) {
            // @ts-ignore
            editor = editorRef.current?.editor;
            editor.container.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            editor.container.removeEventListener("keydown", handleKeyDown);
        };
    }, [editorRef]);

    const tryUpdateJsonData = () => {
        try {
            updateJsonData(JSON.parse(json));
        } catch (e) {
            console.log(
                "[DEBUG] tryUpdateConfiguratorData failed with error: ",
                e
            );
        }
    };

    const checkHasUnsaved = () => {
        try {
            updateHasUnsaved(
                JSON.stringify(JSON.parse(json)) !==
                    JSON.stringify(JSON.parse(jsonData))
            );
        } catch (e) {
            console.log("[DEBUG] checkHasUnsaved invalid json ", e);
            return false;
        }
    };

    useEffect(() => {
        if (canSave) {
            updateCanSave(false);
            tryUpdateJsonData();
        }
    }, [canSave]);

    useEffect(() => {
        checkHasUnsaved();
    }, [jsonData]);

    useEffect(() => {
        checkHasUnsaved();
    }, [json]);

    return (
        <div className="fullSize">
            <AceEditor
                ref={editorRef}
                value={json}
                mode="json"
                theme={useDarkMode ? "monokai" : "github"}
                onChange={setJson}
                fontSize={14}
                showPrintMargin={true}
                showGutter={true}
                highlightActiveLine={true}
                name="configudator-json-editor"
                setOptions={{
                    enableBasicAutocompletion: false,
                    enableLiveAutocompletion: false,
                    enableSnippets: false,
                    showLineNumbers: true,
                    tabSize: 4
                }}
                editorProps={{ $blockScrolling: true }}
                style={{
                    width: "100%",
                    minHeight: `calc(100% - ${inFullScreen ? "55" : "46"}px)`,
                    maxHeight: `calc(100% - ${inFullScreen ? "55" : "46"}px)`,
                    border: "1px solid #ccc",
                    borderRadius: "10px"
                }}
            />
            <div className="w-100 d-flex justify-content-center align-item-center align-content-center">
                <div className="m-2">
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="json-editor-save-btn">
                                {hasUnsaved
                                    ? "You have unsaved changes!"
                                    : "No changes to save"}
                            </Tooltip>
                        }
                    >
                        <Button
                            size="sm"
                            variant={hasUnsaved ? "warning" : "primary"}
                            disabled={updating || !hasUnsaved}
                            onClick={() => tryUpdateJsonData()}
                        >
                            {updating ? "Updating..." : "Save "}
                            {hasUnsaved ? (
                                <FontAwesomeIcon
                                    icon={faExclamation}
                                    size="xs"
                                />
                            ) : (
                                ""
                            )}
                        </Button>
                    </OverlayTrigger>
                </div>
                {removeJsonData ? (
                    <Button
                        className="m-2 w-25"
                        variant="danger"
                        onClick={() => removeJsonData && removeJsonData()}
                        disabled={updating}
                    >
                        {updating ? "Updating..." : "Remove"}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
